import type { Vec2 } from '../../geo/types';
import type { Ground } from '../life/crowd/walkNet';
import { alongLine, lineOf } from '../life/crowd/walkLine';
import { cargoDeliveryLoop, cargoFootprint, cargoHeadingAt } from '../life/cargoPlan';

/**
 * Pedestrian bridge joins cannot carry a cart. Search actual dry street
 * surfaces with enough room for the wheels and parallel delivery lanes.
 */
export function tradeCartPath(from: Vec2, to: Vec2, ground: Ground): Vec2[] | null {
  const step = 0.5,
    margin = 45,
    radius = 0.96;
  const x0 = Math.floor((Math.min(from[0], to[0]) - margin) / step) * step;
  const y0 = Math.floor((Math.min(from[1], to[1]) - margin) / step) * step;
  const nx = Math.ceil((Math.max(from[0], to[0]) + margin - x0) / step) + 1;
  const ny = Math.ceil((Math.max(from[1], to[1]) + margin - y0) / step) + 1;
  const point = (id: number): Vec2 => [x0 + (id % nx) * step, y0 + Math.floor(id / nx) * step];
  const cache = new Int8Array(nx * ny);
  const room = (p: Vec2) => {
    if (!ground.open(p)) return false;
    for (let i = 0; i < 16; i++) {
      const angle = (i * Math.PI) / 8;
      if (!ground.open([p[0] + Math.cos(angle) * radius, p[1] + Math.sin(angle) * radius]))
        return false;
    }
    return true;
  };
  const open = (id: number) => {
    if (!cache[id]) cache[id] = room(point(id)) ? 1 : -1;
    return cache[id] === 1;
  };
  const clear = (a: Vec2, b: Vec2) => {
    const steps = Math.ceil(Math.hypot(a[0] - b[0], a[1] - b[1]) / step);
    for (let i = 0; i <= steps; i++)
      if (
        !room([
          a[0] + (b[0] - a[0]) * (steps ? i / steps : 0),
          a[1] + (b[1] - a[1]) * (steps ? i / steps : 0),
        ])
      )
        return false;
    return true;
  };
  const attach = (p: Vec2) => {
    const ix = Math.round((p[0] - x0) / step),
      iy = Math.round((p[1] - y0) / step);
    const candidates: number[] = [];
    for (let y = iy - 4; y <= iy + 4; y++)
      for (let x = ix - 4; x <= ix + 4; x++)
        if (x >= 0 && y >= 0 && x < nx && y < ny) candidates.push(y * nx + x);
    candidates.sort(
      (a, b) =>
        Math.hypot(point(a)[0] - p[0], point(a)[1] - p[1]) -
        Math.hypot(point(b)[0] - p[0], point(b)[1] - p[1])
    );
    return candidates.find((id) => open(id) && clear(p, point(id))) ?? -1;
  };
  const start = attach(from),
    end = attach(to);
  if (start < 0 || end < 0) return null;
  const parent = new Int32Array(nx * ny).fill(-1),
    queue = new Int32Array(nx * ny);
  let count = 1,
    first = 0;
  queue[0] = end;
  parent[end] = end;
  while (first < count && parent[start] === -1) {
    const id = queue[first++]!,
      x = id % nx,
      y = Math.floor(id / nx);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [-1, 1],
      [1, -1],
      [-1, -1],
    ] as const) {
      const xx = x + dx,
        yy = y + dy;
      if (xx < 0 || yy < 0 || xx >= nx || yy >= ny) continue;
      const next = yy * nx + xx;
      if (parent[next] !== -1 || !open(next)) continue;
      if (dx !== 0 && dy !== 0 && (!open(y * nx + xx) || !open(yy * nx + x))) continue;
      parent[next] = id;
      queue[count++] = next;
    }
  }
  if (parent[start] === -1) return null;
  const raw: Vec2[] = [from];
  for (let id = start; id !== end; id = parent[id]!) raw.push(point(id));
  raw.push(point(end), to);
  const path: Vec2[] = [from];
  for (let i = 0; i < raw.length - 1;) {
    let j = raw.length - 1;
    while (j > i + 1 && !clear(raw[i]!, raw[j]!)) j--;
    path.push(raw[j]!);
    i = j;
  }
  return path;
}

export function tradeCartRoute(from: Vec2, to: Vec2, ground: Ground) {
  const path = tradeCartPath(from, to, ground);
  if (!path) return null;
  const cart = cargoDeliveryLoop(path, 0.35),
    line = lineOf(cart.map(([x, y]) => [x, y, 0] as const));
  if (!Number.isFinite(line.length) || line.length <= 0) return null;
  const at = { x: 0, y: 0, ground: 0, heading: 0 };
  let arrival = 0,
    nearest = Infinity;
  for (let distance = 0; distance < line.length; distance += 0.5) {
    alongLine(line, distance / line.length, at);
    if (!cargoFootprint([at.x, at.y], cargoHeadingAt(line, distance), 0.82).every(ground.open))
      return null;
    const gap = Math.hypot(at.x - to[0], at.y - to[1]);
    if (gap < nearest) {
      nearest = gap;
      arrival = distance;
    }
  }
  return { cart, arrival };
}
